export interface SignContent {
  problemaTitle: string
  problemaSubtitle: string
  problemas: string[]
  nucleoTitle: string
  nucleoDescription: string
  descubriras: string[]
}

export interface Sign {
  id: string
  name: string
  entidad: string
  arquetipo: string
  colors: {
    primary: string
    secondary: string
    accent: string
  }
  particleType:
    | 'fire'
    | 'earth'
    | 'air'
    | 'water'
    | 'electric'
    | 'smoke'
    | 'light'
    | 'cosmic'
  phrase: string
  symbol: string
  imageUrl: string
  checkoutUrl: string
  transitionGif?: string
  content: SignContent
}

const CDN_BASE = 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files'

export const signs: Sign[] = [
  {
    id: 'aries',
    name: 'Aries',
    entidad: 'Carnero mitológico de fuego',
    arquetipo: 'El Portador del Fuego Primordial',
    colors: {
      primary: '#C41E3A',
      secondary: '#FF6B35',
      accent: '#FF8C42',
    },
    particleType: 'fire',
    phrase: 'Algunos esperan el momento. Aries lo crea.',
    symbol: '♈',
    imageUrl: `${CDN_BASE}/01_ARIES_1.png?v=1778615905`,
    checkoutUrl: 'https://los-12-2.myshopify.com/cart/42759429554270:1',
    transitionGif: `${CDN_BASE}/Create_an_ultra_cinematic_transition_animatio.gif?v=1778622164`,
    content: {
      problemaTitle: 'Aries nació para iniciar.',
      problemaSubtitle: 'Pero a veces confunde acción con impulsividad.',
      problemas: [
        'Empiezas con fuego pero te cuesta sostener lo que comienzas.',
        'Tu impaciencia te hace saltar al siguiente desafío antes de completar el anterior.',
        'Reaccionas antes de pensar y luego lidias con las consecuencias.',
        'Tu necesidad de ser primero a veces te deja solo en la cima.',
      ],
      nucleoTitle: 'El Núcleo Aries',
      nucleoDescription:
        'Una exploración de la naturaleza del Portador del Fuego Primordial. No es un plan de acción. Es una comprensión de por qué tu impulso no es imprudencia, sino tu forma de crear realidad. Por qué tu impaciencia es en realidad hambre de vida. Por qué tu verdadero poder no está en ganar, sino en iniciar.',
      descubriras: [
        'La raíz de tu impulso y por qué necesitas crear constantemente',
        'Tu relación con la acción, la impaciencia y el coraje',
        'Los patrones de inicio y abandono que repites',
        'Tu lado sombrío: impulsividad, ira y cómo canalizarlos',
        'Cómo amas cuando dejas de competir',
        'El ritual del Carnero: integrar tu fuego sin quemarte',
      ],
    },
  },
  {
    id: 'tauro',
    name: 'Tauro',
    entidad: 'Toro celestial de piedra y oro',
    arquetipo: 'El Guardián de la Tierra Sagrada',
    colors: {
      primary: '#4A7C3A',
      secondary: '#8B6914',
      accent: '#D4AF37',
    },
    particleType: 'earth',
    phrase: 'Su fuerza no hace ruido. Se impone.',
    symbol: '♉',
    imageUrl: `${CDN_BASE}/02_TAURO_1.png`,
    checkoutUrl: 'https://los-12-2.myshopify.com/cart/42759431913566:1',
    content: {
      problemaTitle: 'Tauro nació para construir estabilidad.',
      problemaSubtitle:
        'Pero a veces se aferra tanto a lo conocido que termina atrapado dentro de su propia comodidad.',
      problemas: [
        'Te cuesta soltar personas, rutinas y emociones incluso cuando sabes que ya terminaron.',
        'Tu necesidad de seguridad puede convertirse en resistencia al cambio.',
        'Proteges tanto tu paz que a veces levantas muros imposibles de atravesar.',
        'Confundes estabilidad con quedarte donde ya no creces.',
      ],
      nucleoTitle: 'El Núcleo Tauro',
      nucleoDescription:
        'Una exploración de la naturaleza del Guardián de la Tierra Sagrada. No es sobre terquedad. Es sobre comprender por qué necesitas raíces profundas, por qué valoras tanto la estabilidad y cómo tu energía encuentra poder cuando aprende a evolucionar sin perder su esencia.',
      descubriras: [
        'Por qué necesitas sentir estabilidad emocional para avanzar',
        'Tu relación con la seguridad, el placer y el apego',
        'Los patrones de resistencia al cambio que repites',
        'Tu lado sombrío: posesividad, miedo a perder y control emocional',
        'Cómo amas cuando bajas las defensas',
        'El ritual del Toro: construir sin convertirte en piedra',
      ],
    },
  },
  {
    id: 'geminis',
    name: 'Géminis',
    entidad: 'Criatura dual — dos aves/serpientes gemelas astrales',
    arquetipo: 'El Mensajero de los Dos Mundos',
    colors: {
      primary: '#E6C200',
      secondary: '#1E3A8A',
      accent: '#C0C0C0',
    },
    particleType: 'light',
    phrase: 'Donde otros escuchan una voz, Géminis escucha universos.',
    symbol: '♊',
    imageUrl: `${CDN_BASE}/03_GEMINIS_1.png`,
    checkoutUrl: 'https://los-12-2.myshopify.com/cart/42759432241246:1',
    content: {
      problemaTitle: 'Géminis nació para explorar ideas.',
      problemaSubtitle:
        'Pero a veces piensa tanto que termina desconectado de lo que realmente siente.',
      problemas: [
        'Tu mente nunca se detiene.',
        'Saltas entre posibilidades, conversaciones y versiones de ti mismo.',
        'Te adaptas tan rápido que a veces ni tú sabes cuál es tu verdadera voz.',
        'Te escondes detrás de palabras cuando lo que sientes es demasiado profundo.',
      ],
      nucleoTitle: 'El Núcleo Géminis',
      nucleoDescription:
        'Una exploración del Mensajero de los Dos Mundos. No es sobre indecisión. Es sobre comprender tu necesidad de movimiento mental, tu búsqueda constante de significado y la dualidad que vive dentro de ti.',
      descubriras: [
        'La raíz de tu necesidad de explorar constantemente',
        'Tu relación con la comunicación y la identidad',
        'Los patrones de desconexión emocional que repites',
        'Tu lado sombrío: dispersión, ansiedad y evasión',
        'Cómo amas cuando dejas de esconderte detrás de palabras',
        'El ritual de los Gemelos: integrar mente y emoción',
      ],
    },
  },
  {
    id: 'cancer',
    name: 'Cáncer',
    entidad: 'Cangrejo lunar mitológico',
    arquetipo: 'El Guardián de las Mareas Internas',
    colors: {
      primary: '#9CB4D8',
      secondary: '#1E3A8A',
      accent: '#F8F8FF',
    },
    particleType: 'water',
    phrase: 'Protege lo que ama como si fuera un reino.',
    symbol: '♋',
    imageUrl: `${CDN_BASE}/04_CANCER_1.png`,
    checkoutUrl: 'https://los-12-2.myshopify.com/cart/42759432536158:1',
    content: {
      problemaTitle: 'Cáncer nació para sentir profundamente.',
      problemaSubtitle:
        'Pero a veces protege tanto sus emociones que termina aislándose.',
      problemas: [
        'Tu sensibilidad es inmensa.',
        'Recuerdas heridas que otros ya olvidaron.',
        'Das amor con intensidad, pero también temes ser lastimado.',
        'Te refugias en tu caparazón cuando deberías permitirte ser visto.',
      ],
      nucleoTitle: 'El Núcleo Cáncer',
      nucleoDescription:
        'Una exploración del Guardián de las Mareas Internas. No es debilidad emocional. Es comprender por qué sientes tan profundo, por qué tu memoria emocional nunca desaparece y cómo transformar protección en conexión.',
      descubriras: [
        'La raíz de tu sensibilidad emocional',
        'Tu relación con el apego y la protección',
        'Los patrones emocionales que repites en silencio',
        'Tu lado sombrío: aislamiento, nostalgia y miedo al abandono',
        'Cómo amas cuando te permites confiar',
        'El ritual de la Luna: sanar sin esconderte',
      ],
    },
  },
  {
    id: 'leo',
    name: 'Leo',
    entidad: 'León solar divino',
    arquetipo: 'El Rey Solar',
    colors: {
      primary: '#E6B800',
      secondary: '#1A1A1A',
      accent: '#FFC847',
    },
    particleType: 'fire',
    phrase: 'La presencia no se aprende. Se impone.',
    symbol: '♌',
    imageUrl: `${CDN_BASE}/05_LEO_1.png`,
    checkoutUrl: 'https://los-12-2.myshopify.com/cart/42759432863838:1',
    content: {
      problemaTitle: 'Leo nació para brillar.',
      problemaSubtitle:
        'Pero a veces siente que debe demostrar constantemente su valor para ser amado.',
      problemas: [
        'Tu energía llena cualquier espacio.',
        'Detrás de esa fuerza existe una necesidad profunda de reconocimiento.',
        'Quieres ser visto no solo por lo que haces, sino por quien realmente eres.',
        'Tu orgullo te impide pedir ayuda incluso cuando la necesitas desesperadamente.',
      ],
      nucleoTitle: 'El Núcleo Leo',
      nucleoDescription:
        'Una exploración del Rey Solar. No es ego. Es comprender tu necesidad de expresión, reconocimiento y autenticidad sin depender de aprobación externa.',
      descubriras: [
        'La raíz de tu necesidad de reconocimiento',
        'Tu relación con la presencia y el orgullo',
        'Los patrones de validación que repites',
        'Tu lado sombrío: orgullo herido, dramatización y miedo a ser ignorado',
        'Cómo amas cuando dejas de actuar para impresionar',
        'El ritual del Sol: brillar sin consumirte',
      ],
    },
  },
  {
    id: 'virgo',
    name: 'Virgo',
    entidad: 'Cierva/ave sagrada de mármol y luz',
    arquetipo: 'La Arquitecta del Orden',
    colors: {
      primary: '#D4D4C8',
      secondary: '#556B2F',
      accent: '#D4AF37',
    },
    particleType: 'light',
    phrase: 'Ve el caos oculto donde otros ven normalidad.',
    symbol: '♍',
    imageUrl: `${CDN_BASE}/06_VIRGO_1.png`,
    checkoutUrl: 'https://los-12-2.myshopify.com/cart/42759433125982:1',
    content: {
      problemaTitle: 'Virgo nació para observar y mejorar.',
      problemaSubtitle:
        'Pero a veces analiza tanto que se vuelve imposible descansar.',
      problemas: [
        'Tu mente busca orden constantemente.',
        'Notas detalles que nadie más ve.',
        'Intentas corregir todo porque temes perder el control del caos.',
        'Tu autoexigencia te impide reconocer cuánto ya has logrado.',
      ],
      nucleoTitle: 'El Núcleo Virgo',
      nucleoDescription:
        'Una exploración de la Arquitecta del Orden. No es perfeccionismo vacío. Es comprender por qué necesitas estructura y cómo encontrar equilibrio entre control y paz interior.',
      descubriras: [
        'La raíz de tu necesidad de control',
        'Tu relación con el orden y la autoexigencia',
        'Los patrones mentales que repites',
        'Tu lado sombrío: crítica excesiva y ansiedad',
        'Cómo amas cuando bajas tus estándares imposibles',
        'El ritual de la Tierra Sagrada: construir paz interior',
      ],
    },
  },
  {
    id: 'libra',
    name: 'Libra',
    entidad: 'Cisne/ave celestial de equilibrio',
    arquetipo: 'La Guardiana del Equilibrio',
    colors: {
      primary: '#E8C5C9',
      secondary: '#FFFAF0',
      accent: '#F0E68C',
    },
    particleType: 'air',
    phrase: 'Donde todo se rompe, Libra busca armonía.',
    symbol: '♎',
    imageUrl: `${CDN_BASE}/07LIBRA01_1.png?v=1778623066`,
    checkoutUrl: 'https://los-12-2.myshopify.com/cart/42759433420894:1',
    content: {
      problemaTitle: 'Libra nació para crear equilibrio.',
      problemaSubtitle:
        'Pero a veces sacrifica demasiado de sí mismo para evitar conflicto.',
      problemas: [
        'Buscas armonía constantemente.',
        'Lees emociones y tensiones incluso cuando nadie habla.',
        'Quieres conexión profunda, pero temes decepcionar a los demás.',
        'Te pierdes en lo que otros necesitan antes de saber qué necesitas tú.',
      ],
      nucleoTitle: 'El Núcleo Libra',
      nucleoDescription:
        'Una exploración de la Guardiana del Equilibrio. No es indecisión. Es comprender tu necesidad de armonía, belleza y conexión emocional auténtica.',
      descubriras: [
        'La raíz de tu necesidad de equilibrio',
        'Tu relación con el amor y la validación emocional',
        'Los patrones de complacencia que repites',
        'Tu lado sombrío: evasión y dependencia emocional',
        'Cómo amas cuando dejas de perderte en otros',
        'El ritual de la Balanza: elegirte sin romper la armonía',
      ],
    },
  },
  {
    id: 'escorpio',
    name: 'Escorpio',
    entidad: 'Escorpión abismal cósmico',
    arquetipo: 'El Guardián del Abismo',
    colors: {
      primary: '#6B2C91',
      secondary: '#0A0A0A',
      accent: '#B47BD8',
    },
    particleType: 'smoke',
    phrase: 'No teme a la oscuridad. Aprendió a vivir dentro de ella.',
    symbol: '♏',
    imageUrl: `${CDN_BASE}/08_ESCORPIO_1.png`,
    checkoutUrl: 'https://los-12-2.myshopify.com/cart/42759433683038:1',
    content: {
      problemaTitle: 'Escorpio nació para transformar.',
      problemaSubtitle:
        'Pero a veces siente tan intenso que termina escondiendo partes de sí mismo.',
      problemas: [
        'Tu energía observa todo.',
        'Lees emociones que otros intentan ocultar.',
        'Quieres conexión real, pero temes entregar completamente el control.',
        'Tu intensidad es tu poder, pero también tu refugio.',
      ],
      nucleoTitle: 'El Núcleo Escorpio',
      nucleoDescription:
        'Una exploración del Guardián del Abismo. No es oscuridad destructiva. Es comprender por qué sientes tan profundo, por qué tu energía transforma todo lo que toca y cómo convertir intensidad en poder consciente.',
      descubriras: [
        'La raíz de tu intensidad emocional',
        'Tu relación con el control y la vulnerabilidad',
        'Los patrones de protección emocional que repites',
        'Tu lado sombrío: obsesión, celos y miedo a traición',
        'Cómo amas cuando dejas de defenderte constantemente',
        'El ritual del Escorpión: transformar dolor en poder',
      ],
    },
  },
  {
    id: 'sagitario',
    name: 'Sagitario',
    entidad: 'Centauro/cierva-fénix astral',
    arquetipo: 'El Arquero del Horizonte',
    colors: {
      primary: '#3D5AC9',
      secondary: '#FF6347',
      accent: '#FFD700',
    },
    particleType: 'fire',
    phrase: 'No nació para quedarse. Nació para buscar.',
    symbol: '♐',
    imageUrl: `${CDN_BASE}/09_SAGITARIO_1.png`,
    checkoutUrl: 'https://los-12-2.myshopify.com/cart/42759433945182:1',
    content: {
      problemaTitle: 'Sagitario nació para expandirse.',
      problemaSubtitle:
        'Pero a veces huye antes de enfrentar lo que realmente siente.',
      problemas: [
        'Tu alma necesita movimiento.',
        'Buscas significado constantemente.',
        'Temes sentirte atrapado emocionalmente o perder tu libertad.',
        'Confundes huir con buscar cuando algo se vuelve demasiado real.',
      ],
      nucleoTitle: 'El Núcleo Sagitario',
      nucleoDescription:
        'Una exploración del Arquero del Horizonte. No es irresponsabilidad. Es comprender tu necesidad de exploración, verdad y expansión emocional.',
      descubriras: [
        'La raíz de tu necesidad de libertad',
        'Tu relación con aventura y compromiso',
        'Los patrones de escape que repites',
        'Tu lado sombrío: evasión emocional y exceso de independencia',
        'Cómo amas cuando decides quedarte',
        'El ritual del Arquero: encontrar libertad sin huir',
      ],
    },
  },
  {
    id: 'capricornio',
    name: 'Capricornio',
    entidad: 'Cabra titánica de montaña y obsidiana',
    arquetipo: 'El Titán de la Cima',
    colors: {
      primary: '#5C6B73',
      secondary: '#C0C0C0',
      accent: '#A8B5BD',
    },
    particleType: 'earth',
    phrase: 'Mientras otros sueñan, Capricornio construye.',
    symbol: '♑',
    imageUrl: `${CDN_BASE}/010CAPRICORNIO_1.png?v=1778623139`,
    checkoutUrl: 'https://los-12-2.myshopify.com/cart/42759434240094:1',
    content: {
      problemaTitle: 'Capricornio nació para construir algo eterno.',
      problemaSubtitle:
        'Pero a veces carga tanto peso que olvida cómo descansar.',
      problemas: [
        'Tu energía resiste incluso cuando todo se derrumba.',
        'Te exiges constantemente.',
        'Quieres control porque temes fracasar frente a los demás.',
        'Tu fortaleza es real, pero también tu cárcel.',
      ],
      nucleoTitle: 'El Núcleo Capricornio',
      nucleoDescription:
        'Una exploración del Titán de la Cima. No es frialdad. Es comprender tu necesidad de estructura, logro y control emocional.',
      descubriras: [
        'La raíz de tu necesidad de éxito',
        'Tu relación con responsabilidad y control',
        'Los patrones de autoexigencia que repites',
        'Tu lado sombrío: aislamiento emocional y rigidez',
        'Cómo amas cuando permites vulnerabilidad',
        'El ritual de la Montaña: construir sin destruirte',
      ],
    },
  },
  {
    id: 'acuario',
    name: 'Acuario',
    entidad: 'Criatura aérea portadora de agua cósmica',
    arquetipo: 'El Rebelde del Cielo',
    colors: {
      primary: '#1FA8DC',
      secondary: '#00CED1',
      accent: '#9370DB',
    },
    particleType: 'electric',
    phrase: 'No vino a encajar. Vino a alterar el sistema.',
    symbol: '♒',
    imageUrl: `${CDN_BASE}/011ACUARIO_1.png?v=1778623164`,
    checkoutUrl: 'https://los-12-2.myshopify.com/cart/42759435026526:1',
    content: {
      problemaTitle: 'Acuario nació para pensar diferente.',
      problemaSubtitle:
        'Pero a veces se desconecta emocionalmente para proteger su individualidad.',
      problemas: [
        'Tu mente vive adelantada al presente.',
        'Observas el mundo desde afuera incluso cuando estás dentro.',
        'Quieres conexión real, pero temes perder tu libertad mental.',
        'Tu independencia es tu fortaleza y también tu distancia.',
      ],
      nucleoTitle: 'El Núcleo Acuario',
      nucleoDescription:
        'Una exploración del Rebelde del Cielo. No es frialdad. Es comprender tu necesidad de independencia, innovación y autenticidad emocional.',
      descubriras: [
        'La raíz de tu necesidad de libertad mental',
        'Tu relación con individualidad y conexión',
        'Los patrones de desapego emocional que repites',
        'Tu lado sombrío: desconexión y rebeldía defensiva',
        'Cómo amas cuando permites cercanía real',
        'El ritual del Portador Celestial: conectar sin perderte',
      ],
    },
  },
  {
    id: 'piscis',
    name: 'Piscis',
    entidad: 'Peces cósmicos / dragón acuático astral',
    arquetipo: 'El Guardián del Mar Astral',
    colors: {
      primary: '#4682B4',
      secondary: '#40E0D0',
      accent: '#B19CD9',
    },
    particleType: 'water',
    phrase: 'Hay mundos que solo existen dentro de Piscis.',
    symbol: '♓',
    imageUrl: `${CDN_BASE}/012PISCIS.png?v=1778623410`,
    checkoutUrl: 'https://los-12-2.myshopify.com/cart/42759436075102:1',
    content: {
      problemaTitle: 'Piscis nació para sentir más allá de lo visible.',
      problemaSubtitle:
        'Pero a veces absorbe tanto del mundo que termina perdiéndose dentro de él.',
      problemas: [
        'Tu energía es profundamente intuitiva.',
        'Sientes emociones incluso cuando nadie las expresa.',
        'Quieres salvar, sanar y conectar… incluso cuando eso te destruye.',
        'Te disuelves en los demás antes de recordar dónde terminas tú.',
      ],
      nucleoTitle: 'El Núcleo Piscis',
      nucleoDescription:
        'Una exploración del Guardián del Mar Astral. No es debilidad. Es comprender tu sensibilidad extrema, tu intuición y tu conexión con lo invisible.',
      descubriras: [
        'La raíz de tu sensibilidad intuitiva',
        'Tu relación con emociones y límites',
        'Los patrones de sacrificio emocional que repites',
        'Tu lado sombrío: evasión, idealización y agotamiento emocional',
        'Cómo amas cuando aprendes a proteger tu energía',
        'El ritual del Océano Astral: sentir sin ahogarte',
      ],
    },
  },
]

export const HERO_BACKGROUND_IMAGE = `${CDN_BASE}/00_LOS_12_1_c39dfca6-b1ea-480d-b79c-201898dbd509.png?v=1778618969`

export const HERO_BACKGROUND_IMAGE_MOBILE = `${CDN_BASE}/ChatGPT_Image_12_may_2026_08_45_23_p.m._1_1.png?v=1778633220`

export const getSignById = (id: string): Sign | undefined => {
  return signs.find((sign) => sign.id === id)
}
